"use client";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';

// Route mapping for Arabic labels
const routeLabels = {
    '/': 'الرئيسية',
    '/sections': 'الأقسام',
    '/sections/articles': 'المقالات',
    '/sections/articles': 'المقالات',
    '/sections/podcasts': 'البودكاست',
    '/sections/challenges': 'التحديات',
    '/sections/templates': 'القوالب',
    '/sections/plugins': 'الإضافات',
    '/sections/jobs': 'الوظائف',
    '/sections/shorts': 'الشورتات',
    '/sessions': 'الجلسات',
    '/services': 'الخدمات',
    '/services/courses': 'الدورات التعليمية',
    '/services/quizzes': 'الاختبارات',
    '/services/glossary': 'القاموس',
    '/services/calc': 'تسعير',
    '/services/design-reviews': 'مراجعات التصميم',
    '/account': 'الحساب',
    '/lnk': 'إختصار الروابط',
};

/**
 * Breadcrumbs Component
 * @param {Array} items - Optional custom breadcrumb items. If not provided, will auto-generate from pathname
 * @param {Object} customLabels - Optional custom labels for specific routes
 * @param {string} currentPageTitle - Optional title for the current page (last breadcrumb)
 */
export default function BreadcrumbsNav({ items, customLabels = {}, currentPageTitle }) {
    const pathname = usePathname();

    // Generate breadcrumbs from pathname if items not provided
    const breadcrumbs = useMemo(() => {
        if (items && items.length > 0) {
            return items;
        }

        // Split pathname into segments
        const segments = pathname.split('/').filter(Boolean);
        const crumbs = [];

        // Always add home
        crumbs.push({
            label: routeLabels['/'] || 'الرئيسية',
            href: '/',
        });

        // Build path progressively
        let currentPath = '';
        segments.forEach((segment, index) => {
            currentPath += `/${segment}`;
            const isLast = index === segments.length - 1;

            // Get label from customLabels, routeLabels, or use segment
            const label = customLabels[currentPath] || 
                         routeLabels[currentPath] || 
                         segment.split('-').map(word => 
                             word.charAt(0).toUpperCase() + word.slice(1)
                         ).join(' ');

            crumbs.push({
                label: isLast && currentPageTitle ? currentPageTitle : label,
                href: isLast ? null : currentPath, // Last item is not a link
            });
        });

        return crumbs;
    }, [pathname, items, customLabels, currentPageTitle]);

    // Don't show breadcrumbs on home page
    if (pathname === '/' && !items) {
        return null;
    }

    return (
        <div aria-label="breadcrumb" className="breadcrumb-nav container mx-auto">
            <div className="container">
                <ol className="breadcrumb mb-0 py-2 justify-content-center">
                    {breadcrumbs.map((crumb, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return (
                            <li
                                key={crumb.href || index}
                                className={`breadcrumb-item ${isLast ? 'active' : ''}`}
                                aria-current={isLast ? 'page' : undefined}
                            >
                                {isLast ? (
                                    <span className="text-truncate d-inline-block" style={{ maxWidth: '300px' }}>
                                        {crumb.label}
                                    </span>
                                ) : (
                                    <Link href={crumb.href} className="text-decoration-none text-muted">
                                        {crumb.label}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}
